import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { setToken, setClinic, isAuthenticated } from '../utils/auth';
import { getErrorMessage } from '../utils/errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/logo-transparent.png';

const Register = () => {
  const navigate = useNavigate();
  const [clinicName, setClinicName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }

    // Fetch clinic list
    console.log('Fetching clinics from:', `${BACKEND_URL}/api/clinics/list`);
    fetch(`${BACKEND_URL}/api/clinics/list`)
      .then(res => res.json())
      .then(data => {
        console.log('Clinics loaded:', data.clinics?.length);
        setClinics(data.clinics || []);
      })
      .catch(error => {
        console.error('Error fetching clinics:', error);
        toast.error('Failed to load clinic list');
      });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!clinicName) {
      toast.error('Please select a clinic');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          clinic_name: clinicName, 
          email, 
          password
        }),
      });

      // Clone response to avoid "body disturbed" error with tracking scripts
      const responseClone = response.clone();
      
      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setClinic(data.clinic);
        toast.success('Registration successful! Welcome!');
        navigate('/dashboard');
      } else {
        const data = await responseClone.json();
        toast.error(getErrorMessage(data.detail, 'Registration failed'));
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left Side - Branding */}
      <div className="hidden md:flex relative items-center justify-center overflow-hidden bg-gradient-to-br from-[hsl(184_45%_88%)] via-[hsl(190_60%_90%)] to-[hsl(200_50%_92%)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(56,189,248,0.1),transparent_50%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.1),transparent_50%)]" />
        <div className="relative text-center px-8 z-10">
          <div className="flex justify-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Pure Souls Logo" 
              className="w-[420px] h-auto drop-shadow-xl"
            />
          </div>
          <div className="inline-block">
            <h2 className="text-2xl font-light tracking-[0.2em] text-gray-700 uppercase mb-2" style={{fontFamily: 'Chivo, sans-serif'}}>Veterinary</h2>
            <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent mb-2"></div>
            <p className="text-lg font-medium text-primary tracking-wider" style={{fontFamily: 'Karla, sans-serif'}}>CLIENT PORTAL</p>
          </div>
        </div>
      </div>
      
      {/* Right Side - Registration Form */}
      <div className="flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-4">
            {/* Mobile Logo */}
            <div className="flex md:hidden justify-center mb-4">
              <img 
                src={LOGO_URL} 
                alt="Pure Souls Logo" 
                className="w-56 h-auto"
              />
            </div>
            <div className="md:hidden text-center mb-4">
              <h2 className="text-lg font-light tracking-wider text-gray-600 uppercase" style={{fontFamily: 'Chivo, sans-serif'}}>Veterinary</h2>
              <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent my-2 mx-auto w-32"></div>
              <p className="text-sm font-medium text-primary tracking-wider" style={{fontFamily: 'Karla, sans-serif'}}>CLIENT PORTAL</p>
            </div>
            <CardTitle className="text-2xl font-semibold">Create Account</CardTitle>
            <CardDescription>Register your clinic to get started</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="clinic">Clinic Name</Label>
                <Select value={clinicName} onValueChange={setClinicName}>
                  <SelectTrigger data-testid="clinic-name-select" className="bg-white">
                    <SelectValue placeholder="Select your clinic" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border shadow-lg max-h-[300px]">
                    {clinics.map((clinic) => (
                      <SelectItem key={clinic} value={clinic} className="cursor-pointer">
                        {clinic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="clinic@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Min. 6 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full mt-2" 
                disabled={loading}
                data-testid="register-form-submit-button"
              >
                {loading ? 'Creating account...' : 'Register'}
              </Button>
            </form>
            <div className="mt-6 text-center text-sm">
              <span className="text-muted-foreground">Already have an account? </span>
              <Link to="/login" className="text-primary hover:underline font-medium">
                Sign in here
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;
